import type { ReactNode } from "react";

type IntegrationConnectionCardProps = {
  icon: ReactNode;
  title: string;
  subtitle?: string;
  subtitleTitle?: string;
  /** Optional footnote under the card (e.g. AERA is source of truth). */
  description?: string;
  actions?: ReactNode;
  showActions?: boolean;
  loading?: boolean;
};

function SkeletonBlock({ className }: { className: string }) {
  return (
    <div
      className={["animate-pulse rounded-md bg-aera-lightgray/50", className].join(
        " ",
      )}
      aria-hidden
    />
  );
}

export function IntegrationConnectionCardSkeleton() {
  return (
    <div
      className="h-full overflow-hidden rounded-2xl border border-aera-lightgray/80 bg-aera-white"
      aria-busy="true"
      aria-label="กำลังโหลด"
    >
      <div className="flex h-full items-center gap-2 md:gap-3 p-3 md:p-4">
        <SkeletonBlock className="h-9 w-9 shrink-0 rounded-xl" />
        <div className="grid min-w-0 flex-1 gap-1.5">
          <SkeletonBlock className="h-4 w-24" />
          <SkeletonBlock className="h-3 w-32" />
        </div>
        <div className="ms-auto flex shrink-0 items-center gap-2">
          <SkeletonBlock className="h-6 w-22 rounded-full" />
          <SkeletonBlock className="h-4.5 w-4.5 rounded" />
        </div>
      </div>
    </div>
  );
}

export function IntegrationConnectionCard({
  icon,
  title,
  subtitle,
  subtitleTitle,
  description,
  actions,
  showActions = true,
  loading = false,
}: IntegrationConnectionCardProps) {
  if (loading) {
    return <IntegrationConnectionCardSkeleton />;
  }

  return (
    <div className="h-full overflow-hidden rounded-2xl border border-aera-lightgray/80 bg-aera-white">
      <div className="flex h-full items-center gap-2 md:gap-3 p-3 md:p-4">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-aera-greenlight">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-aera-charcoal text-nowrap">
            {title}
          </p>
          {subtitle ? (
            <p
              className="mt-0.5 truncate text-xs text-aera-gray"
              title={subtitleTitle ?? subtitle}
            >
              {subtitle}
            </p>
          ) : null}
        </div>
        {showActions && actions ? (
          <div className="ms-auto flex shrink-0 items-center gap-2">{actions}</div>
        ) : null}
      </div>
      {description ? (
        <p className="border-t border-aera-lightgray/70 px-3 pb-3 pt-2 text-[11px] font-medium leading-snug text-aera-gray md:px-4 md:pb-3.5">
          {description}
        </p>
      ) : null}
    </div>
  );
}
